import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';

const formatWon = (amount) => `₩${Number(amount || 0).toLocaleString('ko-KR')}`;

const toDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const TABS = [
  { key: 'daily', label: '일별' },
  { key: 'weekly', label: '주별' },
  { key: 'monthly', label: '월별' },
];

let toastCounter = 0;
const useToast = () => {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((msg, type = 'info') => {
    const id = ++toastCounter;
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3500);
  }, []);
  return { toasts, addToast };
};

const IconCheck = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconXMark = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const IconInfo = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const Toast = ({ toasts }) => (
  <div className="toast-container">
    {toasts.map((t) => (
      <div key={t.id} className={`toast ${t.type}`}>
        <span className="toast-icon">{t.type === 'success' ? <IconCheck /> : t.type === 'error' ? <IconXMark /> : <IconInfo />}</span>
        <div className="toast-content"><div className="toast-message">{t.msg}</div></div>
      </div>
    ))}
  </div>
);

// CSS-only bar chart component
const BarChart = ({ data, labelKey, valueKey, maxValue, barColor = 'var(--primary)', height = 200 }) => {
  const max = maxValue || Math.max(...data.map((d) => d[valueKey] || 0), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: data.length > 15 ? 2 : 6, height, padding: '0 4px' }}>
      {data.map((d, i) => {
        const val = d[valueKey] || 0;
        const pct = Math.max((val / max) * 100, val > 0 ? 2 : 0);
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end', minWidth: 0 }}>
            <div
              className="chart-bar"
              title={`${d[labelKey]}: ${formatWon(val)}`}
              style={{
                width: '100%',
                maxWidth: 40,
                height: `${pct}%`,
                background: barColor,
                borderRadius: '4px 4px 0 0',
                minHeight: val > 0 ? 4 : 0,
                opacity: 0.85,
                cursor: 'pointer',
                animationDelay: `${i * 40}ms`,
              }}
            />
            <div style={{
              fontSize: data.length > 15 ? '0.6rem' : '0.7rem',
              color: 'var(--text-tertiary)',
              marginTop: 6,
              textAlign: 'center',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: '100%',
            }}>
              {d[labelKey]}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ReportsPage = () => {
  const { user } = useAuth();
  const { toasts, addToast } = useToast();
  const storeId = user?.storeId || user?.store_id;

  const [activeTab, setActiveTab] = useState('daily');
  const [isLoading, setIsLoading] = useState(false);

  // Daily state
  const [dailyDate, setDailyDate] = useState(toDateStr(new Date()));
  const [dailyData, setDailyData] = useState([]);

  // Weekly state
  const [weeklyDate, setWeeklyDate] = useState(toDateStr(new Date()));
  const [weeklyData, setWeeklyData] = useState([]);

  // Monthly state
  const now = new Date();
  const [monthlyYear, setMonthlyYear] = useState(now.getFullYear());
  const [monthlyMonth, setMonthlyMonth] = useState(now.getMonth() + 1);
  const [monthlyData, setMonthlyData] = useState([]);

  // Summary
  const [summary, setSummary] = useState({ totalRevenue: 0, orderCount: 0, avgOrderAmount: 0, topMenus: [] });

  const fetchDaily = useCallback(async () => {
    setIsLoading(true);
    try {
      if (storeId) {
        const [reportRes, summaryRes] = await Promise.allSettled([
          api.get(`/admin/reports/${storeId}/daily?date=${dailyDate}`),
          api.get(`/admin/reports/${storeId}/summary`),
        ]);
        if (reportRes.status === 'fulfilled') {
          const d = reportRes.value.data;
          setDailyData(Array.isArray(d) ? d : d.hourly || d.data || []);
        } else {
          setDailyData([]);
        }
        if (summaryRes.status === 'fulfilled') {
          const s = summaryRes.value.data;
          setSummary({
            totalRevenue: s.totalRevenue ?? s.total_revenue ?? 0,
            orderCount: s.orderCount ?? s.order_count ?? 0,
            avgOrderAmount: s.avgOrderAmount ?? s.avg_order_amount ?? 0,
            topMenus: s.topMenus ?? s.top_menus ?? [],
          });
        }
      } else {
        setDailyData([]);
      }
    } catch {
      setDailyData([]);
      addToast('데이터 로드에 실패했습니다.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [storeId, dailyDate, addToast]);

  const fetchWeekly = useCallback(async () => {
    setIsLoading(true);
    try {
      if (storeId) {
        const res = await api.get(`/admin/reports/${storeId}/weekly?date=${weeklyDate}`);
        const d = res.data;
        setWeeklyData(Array.isArray(d) ? d : d.daily || d.data || []);
      } else {
        setWeeklyData([]);
      }
    } catch {
      setWeeklyData([]);
    } finally {
      setIsLoading(false);
    }
  }, [storeId, weeklyDate]);

  const fetchMonthly = useCallback(async () => {
    setIsLoading(true);
    try {
      if (storeId) {
        const res = await api.get(`/admin/reports/${storeId}/monthly?year=${monthlyYear}&month=${monthlyMonth}`);
        const d = res.data;
        setMonthlyData(Array.isArray(d) ? d : d.daily || d.data || []);
      } else {
        setMonthlyData([]);
      }
    } catch {
      setMonthlyData([]);
    } finally {
      setIsLoading(false);
    }
  }, [storeId, monthlyYear, monthlyMonth]);

  useEffect(() => {
    if (activeTab === 'daily') fetchDaily();
    else if (activeTab === 'weekly') fetchWeekly();
    else if (activeTab === 'monthly') fetchMonthly();
  }, [activeTab, fetchDaily, fetchWeekly, fetchMonthly]);

  const dailyTotal = dailyData.reduce((s, d) => s + (d.revenue || 0), 0);
  const weeklyTotal = weeklyData.reduce((s, d) => s + (d.revenue || 0), 0);
  const monthlyTotal = monthlyData.reduce((s, d) => s + (d.revenue || 0), 0);

  if (isLoading && dailyData.length === 0 && weeklyData.length === 0 && monthlyData.length === 0) {
    return (
      <div className="loading-screen" style={{ height: '100vh', background: 'var(--bg)' }}>
        <div className="spinner spinner-dark" style={{ width: 32, height: 32, borderWidth: 3 }} />
        <span>리포트 불러오는 중...</span>
      </div>
    );
  }

  return (
    <div className="page-container">
      <Toast toasts={toasts} />

      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">매출 리포트</h1>
          <p className="page-subtitle">매출 정산 및 통계를 확인합니다</p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="stats-grid">
        <div className="stat-card animate-fade-in-up" style={{ animationDelay: '0ms' }}>
          <div className="stat-icon purple">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
            </svg>
          </div>
          <div className="stat-info">
            <div className="stat-label">총 매출</div>
            <div className="stat-value stat-number">{formatWon(summary.totalRevenue)}</div>
          </div>
        </div>
        <div className="stat-card animate-fade-in-up" style={{ animationDelay: '60ms' }}>
          <div className="stat-icon green">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
              <circle cx="3" cy="6" r="1" fill="currentColor"/><circle cx="3" cy="12" r="1" fill="currentColor"/><circle cx="3" cy="18" r="1" fill="currentColor"/>
            </svg>
          </div>
          <div className="stat-info">
            <div className="stat-label">주문 수</div>
            <div className="stat-value stat-number">{summary.orderCount}건</div>
          </div>
        </div>
        <div className="stat-card animate-fade-in-up" style={{ animationDelay: '120ms' }}>
          <div className="stat-icon orange">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
          </div>
          <div className="stat-info">
            <div className="stat-label">평균 주문금액</div>
            <div className="stat-value stat-number">{formatWon(summary.avgOrderAmount)}</div>
          </div>
        </div>
        <div className="stat-card animate-fade-in-up" style={{ animationDelay: '180ms' }}>
          <div className="stat-icon blue">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
          </div>
          <div className="stat-info">
            <div className="stat-label">인기 메뉴 1위</div>
            <div className="stat-value" style={{ fontSize: '1.2rem' }}>{summary.topMenus?.[0]?.name || '-'}</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-bar">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Daily tab */}
      {activeTab === 'daily' && (
        <div className="card" style={{ marginTop: 8 }}>
          <div className="card-header">
            <span className="card-title">시간대별 매출</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--primary)' }}>{formatWon(dailyTotal)}</span>
              <input
                type="date"
                className="form-input"
                style={{ maxWidth: 170, padding: '6px 10px', fontSize: '0.82rem' }}
                value={dailyDate}
                onChange={(e) => setDailyDate(e.target.value)}
              />
            </div>
          </div>
          <div className="card-body">
            {isLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                <div className="spinner spinner-dark" style={{ width: 28, height: 28, borderWidth: 3 }} />
              </div>
            ) : (
              <BarChart
                data={dailyData.map((d) => ({ ...d, label: `${d.hour}시` }))}
                labelKey="label"
                valueKey="revenue"
                barColor="var(--primary)"
                height={220}
              />
            )}
          </div>
        </div>
      )}

      {/* Weekly tab */}
      {activeTab === 'weekly' && (
        <div className="card" style={{ marginTop: 8 }}>
          <div className="card-header">
            <span className="card-title">일별 매출 (7일)</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--primary)' }}>{formatWon(weeklyTotal)}</span>
              <input
                type="date"
                className="form-input"
                style={{ maxWidth: 170, padding: '6px 10px', fontSize: '0.82rem' }}
                value={weeklyDate}
                onChange={(e) => setWeeklyDate(e.target.value)}
              />
            </div>
          </div>
          <div className="card-body">
            {isLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                <div className="spinner spinner-dark" style={{ width: 28, height: 28, borderWidth: 3 }} />
              </div>
            ) : (
              <BarChart
                data={weeklyData}
                labelKey="label"
                valueKey="revenue"
                barColor="var(--primary)"
                height={220}
              />
            )}
          </div>
        </div>
      )}

      {/* Monthly tab */}
      {activeTab === 'monthly' && (
        <div className="card" style={{ marginTop: 8 }}>
          <div className="card-header">
            <span className="card-title">일별 매출 ({monthlyYear}년 {monthlyMonth}월)</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--primary)' }}>{formatWon(monthlyTotal)}</span>
              <select
                className="form-select"
                style={{ padding: '6px 10px', fontSize: '0.82rem', minWidth: 80 }}
                value={monthlyYear}
                onChange={(e) => setMonthlyYear(Number(e.target.value))}
              >
                {[2024, 2025, 2026].map((y) => (
                  <option key={y} value={y}>{y}년</option>
                ))}
              </select>
              <select
                className="form-select"
                style={{ padding: '6px 10px', fontSize: '0.82rem', minWidth: 70 }}
                value={monthlyMonth}
                onChange={(e) => setMonthlyMonth(Number(e.target.value))}
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>{i + 1}월</option>
                ))}
              </select>
            </div>
          </div>
          <div className="card-body">
            {isLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                <div className="spinner spinner-dark" style={{ width: 28, height: 28, borderWidth: 3 }} />
              </div>
            ) : (
              <BarChart
                data={monthlyData.map((d) => ({ ...d, label: `${d.day}` }))}
                labelKey="label"
                valueKey="revenue"
                barColor="var(--primary)"
                height={220}
              />
            )}
          </div>
        </div>
      )}

      {/* Top 5 popular menus */}
      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-header">
          <span className="card-title">인기 메뉴 TOP 5</span>
        </div>
        <div className="card-body">
          {summary.topMenus && summary.topMenus.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {summary.topMenus.slice(0, 5).map((menu, idx) => {
                const maxCount = summary.topMenus[0]?.count || 1;
                const pct = (menu.count / maxCount) * 100;
                return (
                  <div key={idx} className="animate-fade-in-up" style={{ display: 'flex', alignItems: 'center', gap: 14, animationDelay: `${idx * 60}ms` }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: '0.8rem', flexShrink: 0,
                      background: idx === 0 ? 'var(--primary)' : idx === 1 ? '#D4B84A' : idx === 2 ? '#E8D48A' : 'var(--border)',
                      color: idx < 3 ? '#fff' : 'var(--text-secondary)',
                    }}>
                      {idx + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{menu.name}</span>
                        <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{menu.count}건 · {formatWon(menu.revenue)}</span>
                      </div>
                      <div style={{ background: 'var(--bg)', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                        <div className="progress-fill" style={{ width: `${pct}%`, height: '100%', background: idx === 0 ? 'var(--primary)' : idx === 1 ? '#D4B84A' : '#E8D48A', borderRadius: 4, animationDelay: `${idx * 80}ms` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state" style={{ padding: '30px 0' }}>
              <p>데이터가 없습니다</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
